import React, { useEffect, useState } from 'react';
import { IconDeleteOutline } from 'components/Icons';
import { useForm } from 'react-hook-form';

import { confirmAlert } from 'react-confirm-alert';
import RoleSelector from '../../../components/RoleSelector/RoleSelector';
import './UsersAdmin.scss';
import { RoleLabels, RoleLabelType } from '../../../../constants';
import { useUpdateLocationUser } from 'hooks/useUpdateLocationUser';
import { useRemoveLocationUser } from 'hooks/useRemoveLocationUser';

interface UserLocationRowProps {
  location: HoneLocationUser;
  refetch: () => void;
}

function UserLocationRow({ location, refetch }: UserLocationRowProps) {
  const [emailOptOut, setEmailOptOut] = useState(location.emailOptOut);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      role: {
        label: RoleLabels[location.role as RoleLabelType],
        value: location.role,
      },
    },
  });

  // Sync local state with prop when it changes
  useEffect(() => {
    setEmailOptOut(location.emailOptOut);
  }, [location.emailOptOut]);

  useEffect(() => {
    const subscription = watch(() => handleSubmit(changeRoleHandler)());
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const { mutate: updateUser } = useUpdateLocationUser({
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      // Revert optimistic update on error
      setEmailOptOut(location.emailOptOut);
    },
  });

  const { mutate: removeLocationUser } = useRemoveLocationUser({
    onSuccess: () => {
      refetch();
    },
  });

  const handleEmailOptOutChange = () => {
    const newValue = !emailOptOut;
    setEmailOptOut(newValue); // Optimistic update
    updateUser({
      userId: location.userId,
      email: location.email,
      role: location.role,
      emailOptOut: newValue,
      locationId: location.locationId,
    });
  };

  const handleRemoveLocationUser = (userEmail: string, loc: HoneLocationUser) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-ui">
            <h3>
              Are you sure you want to remove '{userEmail}' from location: {loc.locationName}?
            </h3>
            <button className="button BKForm-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button BKForm-btn"
              onClick={() => {
                onClose();
                removeLocationUser({
                  userId: loc.userId,
                  email: userEmail,
                  locationId: loc.locationId,
                });
              }}
            >
              Remove User
            </button>
          </div>
        );
      },
    });
  };

  const changeRoleHandler = (formData: any) => {
    updateUser({
      userId: location.userId,
      email: location.email,
      role: formData.role.value,
      emailOptOut: location.emailOptOut,
      locationId: location.locationId,
    });
  };

  return (
    <tr key={location.locationId} className="users-admin-container__table-content-row">
      <td className="users-admin-container__table-content-row-cell">{location.locationName}</td>
      <td className="users-admin-container__table-content-row-cell">
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(changeRoleHandler);
          }}
        >
          <RoleSelector resetAfterSuccess={false} control={control} name="role" rules={{ required: true }} />
        </form>
      </td>
      <td className="users-admin-container__table-content-row-cell">
        <input type="checkbox" checked={emailOptOut} onChange={handleEmailOptOutChange} />
      </td>

      <td className="users-admin-container__table-content-row-cell">
        <button
          className="button BKForm-btn-alert p-1"
          type="button"
          onClick={() => location && handleRemoveLocationUser(location.email, location)}
        >
          <IconDeleteOutline />
        </button>
      </td>
    </tr>
  );
}

export default UserLocationRow;
